import React, {
  useState,
  useCallback,
  useEffect,
  ForwardRefExoticComponent,
} from 'react';
import { useDispatch } from 'react-redux';
import useShallowEqualSelector from './useShallowEqualSelector';
import { useTheme } from 'styled-components';
import { useIntl } from 'react-intl';
import { showDialog } from 'redux/duck/dialogDuck';
import { fetchMedicalStaffsRequested } from 'redux/duck/medicalStaffsDuck';
import Const from 'constant/Const';
import Dropdown from 'component/ui/dropdown/Dropdown';
import usePrevious from './usePrevious';

/**
 * @typedef initDoctor
 * @property {string} key
 * @property {string} label
 * @property {string} value
 *
 */
/**
 *
 * @param {string} placeholder
 * @param {initDoctor | null} initDoctor
 * @param {{disabled: boolean}} useDoctorsDropdown option
 * @returns {[initDoctor, import('react').ForwardRefExoticComponent]}
 */
// * @returns {number}
function useDoctorsDropdown(
  placeholder,
  initDoctor = null,
  { disabled } = { disabled: false }
) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const intl = useIntl();
  const authState = useShallowEqualSelector((state) => state.authReducer);
  const medicalStaffFetchState = useShallowEqualSelector(
    (state) => state.medicalStaffsReducer.fetch
  );
  const [selectedDoctor, setSelectedDoctor] = useState(initDoctor);

  const callbackFetchDoctors = () =>
    dispatch(
      fetchMedicalStaffsRequested(
        authState.user.hospital?.hid,
        1,
        1000,
        '',
        Const.STAFF_TYPE.DOCTOR.value
      )
    );

  const callbackShowDialog = (dialogKey, params, callback) => {
    dispatch(showDialog(dialogKey, params, callback));
  };

  const prevInitDoctor = usePrevious(initDoctor);
  useEffect(() => {
    /**
     * case1: null -> null
     * case2: null -> o
     * case3: o -> null
     * case4: o -> o
     * case1의 경우 시작부터 null로 시작했다는 의미이므로,
     * 이미 mount시 실행되는 useEffect로 인해 처방의선택이 렌더링되어있을 것이므로 상관없음
     * case2의 경우 undefined !== 10 이므로 true
     * case3의 경우 10 !== undefined이므로 true
     * case4의 경우 같다면 실행x 다르다면 실행 o
     */
    if (prevInitDoctor?.label !== initDoctor?.label) {
      callbackFetchDoctors();
      setSelectedDoctor(initDoctor);
    }
  }, [initDoctor]);

  useEffect(() => {
    callbackFetchDoctors();
  }, []);

  const renderDoctorsDropdown = React.forwardRef((props, ref) => (
    <Dropdown
      disabled={disabled}
      style={props.style}
      placeholder={placeholder}
      items={[
        ...medicalStaffFetchState.data.map((doctor) => ({
          key: doctor.username,
          label: doctor.firstName,
          value: doctor.username,
        })),
        {
          key: 'addNewDocter',
          label: intl.formatMessage({
            id: '99-useDoctorsDropdown-Dropdown-DropdownItem-addNewDoctor',
            description: '의사 선택 Dropdown 의 의사 추가 옵션',
            defaultMessage: '의사 추가하기',
          }),
          value: 'addNewDocter',
          textColor: theme.color.PRIMARY_BLUE,
        },
      ]}
      selectedItem={selectedDoctor}
      onChange={(item, index) => {
        if (item.value === 'addNewDocter') {
          callbackShowDialog('NewDoctorDialog', {}, (newDoctor) => {
            setSelectedDoctor({
              label: newDoctor.firstName,
              value: newDoctor.username,
            });
            callbackFetchDoctors();
          });
        } else {
          setSelectedDoctor(item);
        }
      }}
      anchorContainerStyle={{
        padding: '8px 12px',
        minWidth: 198 - 24 - 2,
        width: '100%',
        ...props.anchorContainerStyle,
      }}
      itemContainerStyle={{
        borderColor: theme.color.MEDIUM_LIGHT,
        bottom: props.dropToTop ? 35 : null,
        maxHeight: 148.8, // 24.8 * 6
        overflowY: 'scroll',
        justifyContent: 'initial',
        ...props.itemContainerStyle,
      }}
      dropToTop={props.dropToTop}
    />
  ));

  if (selectedDoctor === null) return [null, renderDoctorsDropdown];

  return [selectedDoctor, renderDoctorsDropdown];
}

export default useDoctorsDropdown;
